import type { AddVerdict } from "./betting-bus";

/** A betslip leg, identified the way Digitain identifies it. */
export interface StakePair {
  stakeId: number;
  eventId: number;
}

/** What `addStakes` did with one suggestion's legs. */
export interface PostOutcome {
  /** Legs THIS call actually posted `toggleStake` for. */
  posted: StakePair[];
  /** Legs skipped because an earlier add already owns them — either confirmed in
   *  the coupon or still awaiting an answer. This call must NOT mutate these. */
  alreadyIn: StakePair[];
}

/** The mutations the caller should apply, and what to report. */
export interface AddResolution {
  confirm: StakePair[];
  forget: StakePair[];
  retract: StakePair[];
  added: boolean;
  landedCount: number;
}

/**
 * Decide what to do after posting an add — the whole decision layer, as a pure
 * function so it can be tested without a DOM, a renderer, or a live operator.
 *
 * THE INVARIANT, and the reason this is not inlined in index.ts:
 * **every returned list is a subset of `posted`.** A leg in `alreadyIn` belongs
 * to an earlier add and is never confirmed, forgotten or retracted here.
 * Violating that is not a cosmetic bug: `toggleStake` is symmetric, so
 * retracting a leg an earlier add put in the coupon TOGGLES IT OUT, silently
 * removing a selection the user deliberately made. (That is exactly what the
 * first cut of this fix did — `addStakes` returned every valid leg rather than
 * the ones it posted, so a combo overlapping an earlier single swept the single
 * away. Caught in review, 2026-08-03.)
 *
 * `landedCount` is reported over ALL legs of the suggestion, because the
 * question it answers is "how much of this pick is in the coupon", not "how much
 * did this particular call put there".
 */
export function resolveAdd(args: {
  outcome: PostOutcome;
  /** Stake ids the OPERATOR says are in the coupon, over posted + alreadyIn. */
  landedIds: number[];
  verdict: AddVerdict;
  /** Result of the booking mint. Only consulted on the `no-bus` path, where the
   *  operator has no ack channel and the mint is the only evidence available. */
  mintOk?: boolean;
}): AddResolution {
  const { outcome, landedIds, verdict, mintOk = false } = args;
  const landed = new Set(landedIds);
  const landedCount = landedIds.length;
  const mine = outcome.posted;

  if (verdict === "confirmed") {
    return { confirm: mine, forget: [], retract: [], added: true, landedCount };
  }

  if (verdict === "no-bus") {
    // No ack channel (Hannibalbet's cross-origin iframe). Silence here is not
    // refusal, so `landedIds` is empty for a reason that says nothing about the
    // legs. The mint is the only evidence; on failure we forget rather than
    // retract, because we cannot know what to retract and a wrong toggle inserts.
    return mintOk
      ? { confirm: mine, forget: [], retract: [], added: true, landedCount }
      : { confirm: [], forget: mine, retract: [], added: false, landedCount };
  }

  // partial | rejected. Sweep back exactly what the operator took FROM THIS CALL
  // and forget the rest, so a refused leg can be retried instead of being skipped
  // forever as "already in the slip".
  const landedMine = mine.filter((p) => landed.has(p.stakeId));
  const missingMine = mine.filter((p) => !landed.has(p.stakeId));
  return {
    // Confirm before retracting: `retractStakes` only posts for legs it knows are
    // genuinely in the coupon, and that knowledge is the `added` set.
    confirm: landedMine,
    forget: missingMine,
    retract: landedMine,
    added: false,
    landedCount,
  };
}


/** Every watched leg is in the coupon — under its own id, or under the id the
 *  dead-stake repair re-posted for the SAME outcome (`successors`: dead → new).
 *  ⛔ Chrome QA 2026-10-13: without the successor a repaired leg never "landed"
 *  under its old id, the late settle read the acca as partial and swept it. */
export function allWatchedLanded(
  watched: number[],
  landed: number[],
  successors: Map<number, number>,
  landedNow: (ids: number[]) => number[],
): boolean {
  return watched.every((sid) => landed.indexOf(sid) >= 0
    || (successors.has(sid) && landedNow([successors.get(sid)!]).length > 0));
}
